import * as React from 'react';
import { type Table as TanStackTable, type Row } from '@tanstack/react-table';
import { Package, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/Skeleton';

export interface CardField<TData> {
  key: string;
  label?: string;
  render: (row: Row<TData>) => React.ReactNode;
  primary?: boolean;
}

interface DataTableCardsProps<TData> {
  table: TanStackTable<TData>;
  isLoading?: boolean;
  isFetching?: boolean;
  cardFields: CardField<TData>[];
  getImageUrl?: (row: Row<TData>) => string | undefined;
  onRowClick?: (row: Row<TData>) => void;
}

function CardSkeleton() {
  return (
    <div className='rounded-lg border bg-card p-4'>
      <div className='flex items-start gap-3'>
        <Skeleton className='h-10 w-10 rounded-md shrink-0' />
        <div className='flex-1 min-w-0 space-y-2'>
          <Skeleton className='h-4 w-3/4' />
          <Skeleton className='h-3 w-1/2' />
        </div>
      </div>
      <div className='flex items-center gap-4 mt-3 pt-3 border-t'>
        <Skeleton className='h-3 w-16' />
        <Skeleton className='h-3 w-20' />
      </div>
    </div>
  );
}

function DataTableCard<TData>({
  row,
  cardFields,
  getImageUrl,
  onRowClick,
}: {
  row: Row<TData>;
  cardFields: CardField<TData>[];
  getImageUrl?: (row: Row<TData>) => string | undefined;
  onRowClick?: (row: Row<TData>) => void;
}) {
  const imageUrl = getImageUrl?.(row);
  const primaryFields = cardFields.filter((f) => f.primary);
  const secondaryFields = cardFields.filter((f) => !f.primary);

  return (
    <div
      className={cn(
        'w-full rounded-lg border bg-card transition-colors',
        onRowClick && 'cursor-pointer hover:bg-muted/30',
      )}
      role={onRowClick ? 'button' : undefined}
      tabIndex={onRowClick ? 0 : undefined}
      onClick={() => onRowClick?.(row)}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onRowClick) {
          e.preventDefault();
          onRowClick(row);
        }
      }}
    >
      <div className='p-4'>
        <div className='flex items-start gap-3'>
          <div className='shrink-0 rounded-md overflow-hidden border bg-muted w-10 h-10 flex items-center justify-center'>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=''
                className='w-full h-full object-cover'
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <Package className='h-5 w-5 text-muted-foreground' />
            )}
          </div>

          <div className='flex-1 min-w-0'>
            {primaryFields.map((field) => (
              <div key={field.key} className='font-medium text-sm truncate'>
                {field.render(row)}
              </div>
            ))}
          </div>
        </div>

        {secondaryFields.length > 0 && (
          <div className='flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-3 border-t text-sm'>
            {secondaryFields.map((field) => (
              <div key={field.key} className='flex items-center gap-1 text-muted-foreground'>
                {field.label && <span className='text-xs'>{field.label}:</span>}
                <span className='text-xs'>{field.render(row)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function DataTableCards<TData>({
  table,
  isLoading,
  isFetching,
  cardFields,
  getImageUrl,
  onRowClick,
}: DataTableCardsProps<TData>) {
  if (isLoading) {
    return (
      <div className='space-y-3'>
        {Array.from({ length: 5 }).map((_, index) => (
          <CardSkeleton key={index} />
        ))}
      </div>
    );
  }

  const rows = table.getRowModel().rows;

  if (rows.length === 0) {
    return (
      <div className='rounded-lg border bg-card p-8 text-center'>
        <p className='text-muted-foreground'>No results.</p>
      </div>
    );
  }

  const isRefetching = isFetching && !isLoading;

  return (
    <div className='relative w-full space-y-3'>
      {isRefetching && (
        <div className='absolute inset-0 z-20 flex items-center justify-center bg-background/50 rounded-md'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        </div>
      )}
      {rows.map((row) => (
        <DataTableCard
          key={row.id}
          row={row}
          cardFields={cardFields}
          getImageUrl={getImageUrl}
          onRowClick={onRowClick}
        />
      ))}
    </div>
  );
}
